/**
 * SalesAllocationEditBookingDialog — "Edit (Booking)" action for Sales Control ›
 * Sales Allocation, converted 1:1 from Access `frmCtrlSalesQrySaleAlc_EditBK_Add`
 * (subform `frmCtrlSalesQrySaleAlc_EditBK`).
 *
 * Flow (Access btnEditBooking → form open with NO selected row):
 *   1. Header combo `Booking No` (required). RowSource: SELECT DISTINCT booking_no
 *      FROM public_v_delivery_order_scs WHERE booking_no is not null …
 *   2. booking_no_AfterUpdate → load every allocation row for that booking into a
 *      grid (Access requeries the subform off a temp table).
 *   3. Each row shows read-only context (Contract No · DO · Client · Contract
 *      Quality · M. Date · From WH · To WH) and 6 editable columns
 *      (Container No · Seal No · No. bag · Tare weight · Tare carton & pallet ·
 *      Tare cont). Column order = subform Top coords.
 *   4. btnSave_Click → one UPDATE per row over public_sales_allocation_scs
 *      (only the 6 editable columns), WHERE sales_allocation_scs_id = … AND
 *      is_status = 0. MsgBox "Please choose Booking No!" if booking blank.
 *
 * Numeric Standard fields (#,##0.00): tare_weight / tare_carton_pallet / tare_cont
 * are right-aligned, format on blur. number_bags is an integer.
 *
 * Upgrade (không có trong Access): cột Tick chọn dòng — cùng Booking No nhưng chỉ
 * Save các dòng được tick. Mặc định tick sẵn tất cả; dòng bỏ tick bị khoá readonly.
 *
 * BACKEND (live): `allocbulk_router` trong endpoints/sale_controls.py →
 * repo sale_control_pg.py (lookup_booking_nos / get_edit_booking_rows /
 * update_edit_booking_rows). Save = batch UPDATE 6 cột editable + audit,
 * WHERE sales_allocation_scs_id = ? AND is_status = 0 (1:1 btnSave_Click).
 */
import { useEffect, useMemo, useState } from 'react'
import { TEXT_TRUNCATE } from '../../theme/responsive'
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, Table, TableBody, TableCell, TableFooter, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'

import {
  type SaleAlcEditBookingLookupItem,
  type SaleAlcEditBookingPatch,
  type SaleAlcEditBookingWrite,
  useSaleAlcEditBookingLookupBookingNosQuery,
  useLazyGetSaleAlcEditBookingRowsQuery,
  useUpdateSaleAlcEditBookingMutation,
} from '../../store/api/saleControlApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import { DISPLAY_DATE_FORMAT } from '../../utils/date'
import dayjs from 'dayjs'

interface Props {
  open: boolean
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
  /** Booking No của dòng đang focus (nếu có) → mở dialog là load sẵn booking đó.
   *  Access btnEditBooking không pre-fill; đây là tiện ích thêm, giống btnEditMany
   *  (Edit similar DO) pre-fill theo delivery_order của dòng focus. */
  initialBookingNo?: string | null
}

/** Per-row editable draft (the 6 "(Edit)" columns), kept as strings for inputs. */
interface RowDraft {
  container_no: string
  seal_no: string
  number_bags: string
  tare_weight: string
  tare_carton_pallet: string
  tare_cont: string
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const roCellSx: SxProps<Theme> = { fontSize: '0.78rem', whiteSpace: 'nowrap', color: 'text.primary', py: 0.5 }
// Edit-cell input format — copy Form "Edit - Similar DO" (outlined box, minHeight 30, 0.75rem).
// px:1 trên input (thay 14px mặc định) để text không bị hộp outlined che.
const editFieldSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 30, fontSize: '0.75rem' },
  '& .MuiInputBase-input': { py: 0.4, px: 1, fontSize: '0.75rem' },
}
// Body cell của cột Edit — giảm padding ngang để ô outlined rộng hơn.
const editCellSx = (w: number): SxProps<Theme> => ({ minWidth: w, px: 0.75 })
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }
const intProps = { style: { textAlign: 'right' as const }, inputMode: 'numeric' as const }
// Header cell: khoá width để tên cột 1 dòng (KHÔNG wrap 2 dòng).
const hCell = (w: number): SxProps<Theme> => ({ width: w, minWidth: w, whiteSpace: 'nowrap' })
// Tick column (upgrade — chọn dòng nào muốn Edit/Save). Cột sticky trái #1;
// Contract No trở thành sticky-col #2 offset đúng bằng bề rộng cột tick.
const TICK_W = 48
const tickCellSx: SxProps<Theme> = { width: TICK_W, minWidth: TICK_W, p: 0, textAlign: 'center' }
// Footer Total cell — bold, nền opaque, viền trên; override màu chữ MuiTableCell-footer (xám).
// Sticky bottom: 0 → dòng Total luôn hiện ở đáy vùng scroll (không phải scroll mới thấy).
const footCellSx: SxProps<Theme> = {
  fontWeight: 800, whiteSpace: 'nowrap', py: 0.6, color: 'text.primary',
  bgcolor: 'background.paper', borderTop: (t: Theme) => `2px solid ${t.palette.divider}`,
  position: 'sticky', bottom: 0, zIndex: 3,
}
// Corner Total cell (vừa sticky-col trái vừa sticky đáy) → z-index cao hơn để không bị đè.
const footCornerSx: SxProps<Theme> = { ...footCellSx, zIndex: 4 }

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))
const fmtDate = (v: unknown): string => {
  const str = s(v)
  if (!str) return ''
  const d = dayjs(str)
  return d.isValid() ? d.format(DISPLAY_DATE_FORMAT) : str
}

export default function SalesAllocationEditBookingDialog({ open, canEdit, onClose, onSaved, initialBookingNo }: Props) {
  const snack = useSnackbar()
  const [bookingNo, setBookingNo] = useState('')
  const [bkQ, setBkQ] = useState('')
  // Per-row editable draft keyed by sales_allocation_scs_id.
  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({})
  // Upgrade — tick chọn dòng muốn Edit/Save. Chỉ dòng được tick mới cho gõ +
  // mới nằm trong payload Save. Mặc định tick sẵn tất cả khi load booking.
  const [selected, setSelected] = useState<Set<number>>(new Set())

  const bookingNos = useSaleAlcEditBookingLookupBookingNosQuery(bkQ || undefined, { skip: !open })
  const [loadRows, { data: rowsData, isFetching: rowsFetching }] = useLazyGetSaleAlcEditBookingRowsQuery()
  const [saveBooking, { isLoading: saving }] = useUpdateSaleAlcEditBookingMutation()

  const rows = useMemo(() => rowsData?.items ?? [], [rowsData])

  // Total row — sum các cột số editable theo GIÁ TRỊ ĐANG SỬA (drafts) của các
  // dòng ĐƯỢC TICK (= sẽ được Save), cập nhật live khi user gõ. Access subform
  // không có TotalsRow; đây là bổ sung tiện ích.
  const totals = useMemo(() => {
    let bags = 0, tareWeight = 0, tareCartonPallet = 0, tareCont = 0
    for (const r of rows) {
      if (!selected.has(r.sales_allocation_scs_id)) continue
      const d = drafts[r.sales_allocation_scs_id]
      bags += parseNum(d?.number_bags ?? '') ?? 0
      tareWeight += parseNum(d?.tare_weight ?? '') ?? 0
      tareCartonPallet += parseNum(d?.tare_carton_pallet ?? '') ?? 0
      tareCont += parseNum(d?.tare_cont ?? '') ?? 0
    }
    return { bags, tareWeight, tareCartonPallet, tareCont }
  }, [rows, drafts, selected])

  // Reset when the dialog opens (Access deletes the temp table first). Nếu dòng
  // đang focus có Booking No → seed sẵn booking đó (bookingNo khác rỗng sẽ kích
  // effect load rows bên dưới); không có thì mở rỗng như Access.
  useEffect(() => {
    if (!open) return
    setBookingNo((initialBookingNo ?? '').trim())
    setBkQ('')
    setDrafts({})
    setSelected(new Set())
  }, [open, initialBookingNo])

  // booking_no_AfterUpdate → load allocation rows for the chosen booking.
  useEffect(() => {
    if (!open) return
    if (!bookingNo) { setDrafts({}); setSelected(new Set()); return }
    let cancelled = false
    void loadRows(bookingNo).unwrap()
      .then((res) => {
        if (cancelled) return
        const next: Record<number, RowDraft> = {}
        for (const r of res.items) {
          next[r.sales_allocation_scs_id] = {
            container_no: s(r.container_no),
            seal_no: s(r.seal_no),
            number_bags: r.number_bags == null ? '' : String(r.number_bags),
            tare_weight: fmtStd(r.tare_weight),
            tare_carton_pallet: fmtStd(r.tare_carton_pallet),
            tare_cont: fmtStd(r.tare_cont),
          }
        }
        setDrafts(next)
        // Mặc định tick sẵn tất cả dòng của booking (tương thích hành vi cũ).
        setSelected(new Set(res.items.map((r) => r.sales_allocation_scs_id)))
      })
      .catch(() => { if (!cancelled) { setDrafts({}); setSelected(new Set()) } })
    return () => { cancelled = true }
  }, [open, bookingNo, loadRows])

  const setCell = (id: number, key: keyof RowDraft, value: string) =>
    setDrafts((p) => ({ ...p, [id]: { ...p[id], [key]: value } }))
  const blurNum = (id: number, key: keyof RowDraft) =>
    setDrafts((p) => ({ ...p, [id]: { ...p[id], [key]: fmtStd(p[id]?.[key]) } }))

  // Tick chọn dòng để Save.
  const toggleRow = (id: number) =>
    setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n })
  const toggleAll = () =>
    setSelected((p) => (p.size === rows.length ? new Set() : new Set(rows.map((r) => r.sales_allocation_scs_id))))
  const selectedCount = selected.size
  const allSelected = rows.length > 0 && selectedCount === rows.length
  const someSelected = selectedCount > 0 && !allSelected

  // Booking No combo selection (inject current value into options like Combo helper).
  const bkOptions = bookingNos.currentData?.items ?? []
  const bkSel: SaleAlcEditBookingLookupItem | null =
    bookingNo ? bkOptions.find((o) => o.value === bookingNo) ?? { value: bookingNo, label: bookingNo } : null
  // Đang gõ search (bkQ active) → KHÔNG inject bkSel: filterOptions={(x)=>x} sẽ
  // hiển thị cả dòng inject không khớp text (bug "1 dòng đúng + dòng cũ").
  const bkMerged = !bkQ && bkSel && !bkOptions.some((o) => o.value === bkSel.value) ? [bkSel, ...bkOptions] : bkOptions

  const bookingEmpty = !bookingNo.trim()
  const canSave = canEdit && !bookingEmpty && rows.length > 0 && selectedCount > 0 && !rowsFetching && !saving

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (!canSave) return
    // Chỉ ghi các dòng ĐƯỢC TICK (upgrade — cùng Booking No nhưng chọn vài dòng).
    const patches: SaleAlcEditBookingPatch[] = rows
      .filter((r) => selected.has(r.sales_allocation_scs_id))
      .map((r) => {
      const d = drafts[r.sales_allocation_scs_id]
      return {
        sales_allocation_scs_id: r.sales_allocation_scs_id,
        container_no: d?.container_no.trim() || null,
        seal_no: d?.seal_no.trim() || null,
        number_bags: parseNum(d?.number_bags ?? ''),
        tare_weight: parseNum(d?.tare_weight ?? ''),
        tare_carton_pallet: parseNum(d?.tare_carton_pallet ?? ''),
        tare_cont: parseNum(d?.tare_cont ?? ''),
      }
    })
    const payload: SaleAlcEditBookingWrite = { booking_no: bookingNo.trim(), rows: patches }
    try {
      await saveBooking(payload).unwrap()
      snack.success(`Booking ${bookingNo} — ${patches.length} allocation${patches.length > 1 ? 's' : ''} updated`)
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Update allocations failed'))
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { maxHeight: 'min(840px, 94vh)' } }}
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" noWrap title="Edit (Booking) — Sales Allocation" sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700 }}>Edit (Booking) — Sales Allocation</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit allocations.</Alert>}

        {/* Header — Booking No combo (Access FormHeader, required). */}
        <Box sx={{ maxWidth: 520, mb: 1.5 }}>
          <Autocomplete
            size={SM}
            options={bkMerged}
            value={bkSel}
            loading={bookingNos.isFetching}
            onChange={(_e, v) => { setBookingNo(v?.value ?? ''); setBkQ('') }}
            onInputChange={(_e, v, reason) => { if (reason === 'input') setBkQ(v) }}
            getOptionLabel={(o) => (o ? String(o.label ?? o.value) : '')}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField {...params} label="Booking No" required error={bookingEmpty} />
            )}
          />
          {bookingNo && !rowsFetching && rows.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Tick rows to save — <b>{selectedCount}</b>/{rows.length} selected.
            </Typography>
          )}
        </Box>

        {/* Detail — allocation rows for the chosen booking. Style list-page theo
            skill /convert-table: wrapper app-dataframe (border/rounded) + header
            gradient vàng app-table-header-cell sticky + hover + freeze cột định
            danh Contract No (app-sticky-col). Columns ordered by subform Top
            coords: read-only context, then the 6 "(Edit)" columns. */}
        {bookingNo && (
          <Box className="app-dataframe app-dataframe--compact">
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 1520 }}>
              <TableHead>
                <TableRow>
                  <TableCell className="app-table-header-cell app-sticky-col" sx={tickCellSx}>
                    <Checkbox size="small" sx={{ p: 0.25 }}
                      checked={allSelected} indeterminate={someSelected}
                      disabled={!canEdit || rows.length === 0}
                      onChange={toggleAll} inputProps={{ 'aria-label': 'Select all rows' }} />
                  </TableCell>
                  <TableCell className="app-table-header-cell app-sticky-col-2" style={{ left: TICK_W }} sx={hCell(110)}>Contract No</TableCell>
                  <TableCell className="app-table-header-cell" sx={hCell(110)}>DO</TableCell>
                  <TableCell className="app-table-header-cell" sx={hCell(190)}>Container No.</TableCell>
                  <TableCell className="app-table-header-cell" sx={hCell(150)}>Seal No.</TableCell>
                  <TableCell className="app-table-header-cell" align="right" sx={hCell(120)}>No. bag</TableCell>
                  <TableCell className="app-table-header-cell" align="right" sx={hCell(140)}>Tare weight</TableCell>
                  <TableCell className="app-table-header-cell" align="right" sx={hCell(180)}>Tare carton &amp; pallet</TableCell>
                  <TableCell className="app-table-header-cell" align="right" sx={hCell(130)}>Tare cont</TableCell>
                  <TableCell className="app-table-header-cell" sx={hCell(160)}>Client</TableCell>
                  <TableCell className="app-table-header-cell" sx={hCell(150)}>Contract Quality</TableCell>
                  <TableCell className="app-table-header-cell" sx={hCell(120)}>M. Date</TableCell>
                  <TableCell className="app-table-header-cell" sx={hCell(140)}>From WH</TableCell>
                  <TableCell className="app-table-header-cell" sx={hCell(140)}>To WH</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rowsFetching && (
                  <TableRow>
                    <TableCell colSpan={14} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={20} />
                    </TableCell>
                  </TableRow>
                )}
                {!rowsFetching && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={14} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No allocations found for this booking.
                    </TableCell>
                  </TableRow>
                )}
                {!rowsFetching && rows.map((r) => {
                  const id = r.sales_allocation_scs_id
                  const d = drafts[id] ?? {
                    container_no: '', seal_no: '', number_bags: '',
                    tare_weight: '', tare_carton_pallet: '', tare_cont: '',
                  }
                  const picked = selected.has(id)
                  // Dòng chưa tick → khoá readonly các ô editable (chỉ Edit dòng đã chọn).
                  const cellDisabled = !canEdit || !picked
                  return (
                    <TableRow key={id} hover>
                      <TableCell className="app-sticky-col" sx={tickCellSx}>
                        <Checkbox size="small" sx={{ p: 0.25 }}
                          checked={picked} disabled={!canEdit}
                          onChange={() => toggleRow(id)}
                          inputProps={{ 'aria-label': `Select ${s(r.sale_contract)}` }} />
                      </TableCell>
                      <TableCell className="app-sticky-col-2" style={{ left: TICK_W }} sx={roCellSx}>{s(r.sale_contract)}</TableCell>
                      <TableCell sx={roCellSx}>{s(r.delivery_order)}</TableCell>
                      <TableCell sx={editCellSx(190)}>
                        <TextField fullWidth size={SM} sx={editFieldSx}
                          value={d.container_no} disabled={cellDisabled}
                          onChange={(e) => setCell(id, 'container_no', e.target.value)} />
                      </TableCell>
                      <TableCell sx={editCellSx(150)}>
                        <TextField fullWidth size={SM} sx={editFieldSx}
                          value={d.seal_no} disabled={cellDisabled}
                          onChange={(e) => setCell(id, 'seal_no', e.target.value)} />
                      </TableCell>
                      <TableCell sx={editCellSx(120)}>
                        <TextField fullWidth size={SM} sx={editFieldSx}
                          value={d.number_bags} disabled={cellDisabled} inputProps={intProps}
                          onChange={(e) => setCell(id, 'number_bags', e.target.value)} />
                      </TableCell>
                      <TableCell sx={editCellSx(140)}>
                        <TextField fullWidth size={SM} sx={editFieldSx}
                          value={d.tare_weight} disabled={cellDisabled} inputProps={numProps}
                          onChange={(e) => setCell(id, 'tare_weight', e.target.value)}
                          onBlur={() => blurNum(id, 'tare_weight')} />
                      </TableCell>
                      <TableCell sx={editCellSx(180)}>
                        <TextField fullWidth size={SM} sx={editFieldSx}
                          value={d.tare_carton_pallet} disabled={cellDisabled} inputProps={numProps}
                          onChange={(e) => setCell(id, 'tare_carton_pallet', e.target.value)}
                          onBlur={() => blurNum(id, 'tare_carton_pallet')} />
                      </TableCell>
                      <TableCell sx={editCellSx(130)}>
                        <TextField fullWidth size={SM} sx={editFieldSx}
                          value={d.tare_cont} disabled={cellDisabled} inputProps={numProps}
                          onChange={(e) => setCell(id, 'tare_cont', e.target.value)}
                          onBlur={() => blurNum(id, 'tare_cont')} />
                      </TableCell>
                      <TableCell sx={roCellSx}>{s(r.client)}</TableCell>
                      <TableCell sx={roCellSx}>{s(r.contract_quality)}</TableCell>
                      <TableCell sx={roCellSx}>{fmtDate(r.m_date)}</TableCell>
                      <TableCell sx={roCellSx}>{s(r.from_warehouse)}</TableCell>
                      <TableCell sx={roCellSx}>{s(r.to_warehouse)}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
              {!rowsFetching && rows.length > 0 && (
                <TableFooter>
                  <TableRow>
                    <TableCell className="app-sticky-col" sx={footCornerSx} />
                    <TableCell className="app-sticky-col-2" style={{ left: TICK_W }} sx={footCornerSx}>Total</TableCell>
                    <TableCell sx={footCellSx} />
                    <TableCell sx={footCellSx} />
                    <TableCell sx={footCellSx} />
                    <TableCell sx={footCellSx} align="right">{totals.bags.toLocaleString('en-US')}</TableCell>
                    <TableCell sx={footCellSx} align="right">{fmtStd(totals.tareWeight)}</TableCell>
                    <TableCell sx={footCellSx} align="right">{fmtStd(totals.tareCartonPallet)}</TableCell>
                    <TableCell sx={footCellSx} align="right">{fmtStd(totals.tareCont)}</TableCell>
                    <TableCell sx={footCellSx} />
                    <TableCell sx={footCellSx} />
                    <TableCell sx={footCellSx} />
                    <TableCell sx={footCellSx} />
                    <TableCell sx={footCellSx} />
                  </TableRow>
                </TableFooter>
              )}
              </Table>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
